//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import OtAdminNumberField from '@react/components/OtAdminNumberField'
import { fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { OtStoreState, useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { dynamicOffChainDepositTx } from '@react/signedTxs/dynamicOffChainDeposit'
import { logOT } from '@shared/loggingShared'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { EthAddr, NumberFormatEnum, PoolAccountAccessPair } from '@shared/SchemaTypes'

export default function DynamicOffchainDepositForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const companyProfiles = useOtSelector((state: OtStoreState) => state.poolState.companyProfiles)
  const [assetValue, setAssetValue] = React.useState<string>('')
  const [isConfirmed, setIsConfirmed] = React.useState(false)

  const [participants, setParticipants] = React.useState<PoolAccountAccessPair[]>([])
  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>('')

  const dispatch = useOtDispatch()

  const [errorAmount, setErrorAmount] = React.useState<string | undefined>(undefined)

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const setDepositAndMint = (): void => {
    setIsSubmitted(true)
    const assets = BigInt(Math.floor(Number(assetValue.replace(/,/g, '')) * 1e6))
    dispatch(
      dynamicOffChainDepositTx({
        poolAddr,
        lenderAddr: selectedParticipant,
        assets,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  function isPositiveNumeric(str: string): boolean {
    const num = Number(str)
    const isValid = !isNaN(num) && isFinite(num) && num > 0

    return isValid
  }
  const disableButton = !(isPositiveNumeric(assetValue) && selectedParticipant !== '')

  useEffect(() => {
    if (providerReadOnly && poolAddr) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_participants) => {
          logOT.debug('all users', _participants)
          setParticipants(_participants)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }, [providerReadOnly, poolAddr])

  const getCompanyName = (userAddr: EthAddr): string => {
    const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(userAddr))

    return companyProfile?.companyName ?? ''
  }
  if (isConfirmed) {
    return (
      <Grid container sx={{ marginTop: 2, flexDirection: 'column', gap: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h4">Confirm amount and lender</Typography>
        </Grid>
        <Typography variant="h4">
          Please confirm the deposit{' '}
          {Number(assetValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {poolCTO.liquidityTokenSymbol}
        </Typography>
        <Typography variant="h4">Please confirm the Lender {selectedParticipant}</Typography>
        <Typography variant="h4" align="left" sx={{ marginBottom: 2 }}>
          {getCompanyName(selectedParticipant)}
        </Typography>
        <Grid item xs={12} sm={12}>
          <Button
            variant={'contained'}
            disabled={isSubmitted || disableButton}
            onClick={() => {
              setDepositAndMint()
            }}
          >
            Approve and Mint
          </Button>
        </Grid>
        {isSubmitted && (
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Submitted ......
          </Typography>
        )}
      </Grid>
    )
  }
  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid item xs={12}>
        <InputLabel id="asset-label">Lender</InputLabel>
        <Select
          labelId="asset-label"
          id="asset-select"
          input={<OutlinedInput id="asset-select" label="Asset" />}
          value={selectedParticipant}
          renderValue={(value: string) => {
            return (
              <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                {getCompanyName(value) + '  '} {value}
              </Typography>
            )
          }}
          onChange={(event: SelectChangeEvent) => {
            logOT.info('event.target.value', event.target.value)
            setSelectedParticipant(event.target.value as string)
          }}
        >
          {participants?.map((p: PoolAccountAccessPair) => (
            <MenuItem
              value={p.accountAddr}
              key={`asset-option-${p.accountAddr}`}
              style={{
                paddingLeft: 12,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                {getCompanyName(p.accountAddr) + `  ${p.accountAddr}`}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12}>
        <OtAdminNumberField
          id="assetValue"
          inputLabel="Amount Deposited"
          value={assetValue}
          sx={{ width: '300px' }}
          variant="outlined"
          numberFormat={NumberFormatEnum.ERC20}
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setAssetValue(changedValue)

            const assetValueNumber = Number(changedValue)

            logOT.info('assetValueNumber ', assetValueNumber, changedValue)
            if (!isPositiveNumeric(changedValue)) {
              setErrorAmount('Please enter a positive number')
            } else {
              setErrorAmount(undefined)
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Typography variant="bodyBold">
          Please confirm the deposit{' '}
          {Number(assetValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {poolCTO.liquidityTokenSymbol}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={12}>
        <Button
          variant={'contained'}
          disabled={disableButton}
          onClick={() => {
            setIsConfirmed(true)
          }}
        >
          <Typography variant={'buttonBold'}>Confirm Deposit</Typography>
        </Button>
      </Grid>
    </Grid>
  )
}
